import { defineStore } from "pinia";
import { downloadFile } from "@/_helpers/download";
import api from "@/_helpers/api";
import type { Paged, Pagination } from "@/types";
import { FilterContext, useFiltersStore } from "@/_store/filters.module";
import { DeviceAllowBlockListTypes } from "@/constants/devices";
import type { DialogDataConfig } from "@/_store/dialogs.module.ts";
import type {
  DeviceAllowBlockListItem,
  DeviceAllowBlockListResponseItem,
} from "@/_store/endpoint-security/device-allow-block-list.module.ts";
import { axiosInstance } from "@/plugins/https.ts";

/**
 * Defines the shape of the allow/block list store's state.
 */
export interface MspDeviceAllowBlockListModuleState {
  allowBlockList: Paged<DeviceAllowBlockListResponseItem>;
  pagination: Pagination;
  loading: boolean;
}

/**
 * Defines the configuration options for creating a store instance.
 * This allows for dynamic IDs, filter contexts, and feature flags.
 */
export interface AllowBlockListStoreOptions {
  id: string; // Unique Pinia store ID
  filterContext:
    | FilterContext.MSP_ENDPOINT_SECURITY_ALLOW_BLOCK_LIST_FILTERS
    | FilterContext.MSP_EDR_ALLOW_BLOCK_LIST_FILTERS; // Filter context for this store instance
  isEdr: boolean; // Flag to distinguish between EDR and Endpoint Security
}

/**
 * The default state for a new allow/block list store instance.
 */
export const defaultAllowBlockListState: MspDeviceAllowBlockListModuleState = {
  allowBlockList: {
    items: [],
    total: 0,
  },
  pagination: {
    page: 0,
    pageSize: 25,
  },
  loading: false,
};

/**
 * Factory function to create a new Pinia store for an allow/block list.
 * @param options - Configuration for the store instance.
 * @returns A Pinia store definition.
 */
export const createAllowBlockListStore = (options: AllowBlockListStoreOptions) => {
  // Determine the default types based on the isEdr flag once.
  const defaultTypes = options.isEdr
    ? [
        DeviceAllowBlockListTypes.EDR_PROCESS,
        DeviceAllowBlockListTypes.EDR_FOLDER,
        DeviceAllowBlockListTypes.EDR_FILE,
      ]
    : [
        DeviceAllowBlockListTypes.PROCESS,
        DeviceAllowBlockListTypes.FOLDER,
        DeviceAllowBlockListTypes.FILE,
      ];

  const defaultTypesString = defaultTypes.join(",");

  return defineStore(options.id, {
    state: (): MspDeviceAllowBlockListModuleState => ({
      ...defaultAllowBlockListState,
    }),
    actions: {
      /**
       * Replace current pagination values.
       * @param pagination New pagination values.
       */
      setPagination(pagination: Pagination): void {
        this.pagination = pagination;
      },

      /**
       * Reset pagination to default values.
       */
      resetPagination(): void {
        this.pagination = { ...defaultAllowBlockListState.pagination };
      },

      /**
       * Fetch allow/block list items using the configured filter context and pagination.
       */
      async getItems() {
        this.loading = true;
        try {
          const filtersStore = useFiltersStore();
          const { search, type } = filtersStore.filters[options.filterContext];
          const filterPayload = {
            search,
            types: type || defaultTypesString,
          };

          const request = {
            ...api.mspEndpointSecurityAllowBlockList(),
            method: "GET",
            params: {
              ...filterPayload,
              ...this.pagination,
            },
          };

          const { data } =
            await axiosInstance.request<Paged<DeviceAllowBlockListResponseItem>>(request);
          this.allowBlockList = data;
        } catch (error) {
          console.error(error);
        } finally {
          this.loading = false;
        }
      },

      /**
       * Create a new allow/block list entry.
       * @param payload - Dialog data containing the item to create.
       */
      async addToAllowBlockList(payload: DialogDataConfig<DeviceAllowBlockListItem>) {
        this.loading = true;
        try {
          const { value, description, blocked, type, valueType } = payload.item;
          const data: DeviceAllowBlockListItem = { value, description, blocked, type };
          if (valueType) data.valueType = valueType;

          const request = {
            ...api.mspEndpointSecurityAllowBlockList(),
            method: "POST",
            data,
          };
          await axiosInstance.request(request);
          await this.getItems(); // Refresh list after adding
        } catch (error) {
          console.error(error);
        } finally {
          this.loading = false;
        }
      },

      /**
       * Upload a CSV file, using the 'isEdr' flag from the factory options.
       * @param file - The CSV file to upload.
       * @returns The Axios response promise for component-level handling (e.g., notifications).
       */
      async importAllowBlockListFromCsv(file: File) {
        const data = new FormData();
        data.append("file", file);
        data.append("isEdrPage", String(options.isEdr));

        const request = {
          ...api.mspImportEndpointSecurityAllowBlockList(),
          data,
        };
        return axiosInstance.request(request);
      },

      /**
       * Download the CSV example template.
       */
      async downloadAllowBlockListExampleCsv() {
        this.loading = true;
        try {
          const request = { ...api.mspEndpointSecurityAllowBlockListExampleCSV() };
          const { data, headers } = await axiosInstance.request(request);
          const blob = new Blob([data], { type: headers["content-type"] as string | undefined });
          const fileName = options.isEdr
            ? "EdrAllowBlockListExample.csv"
            : "EndpointAllowBlockListExample.csv";
          downloadFile(blob, fileName);
        } catch (error) {
          console.error(error);
        } finally {
          this.loading = false;
        }
      },

      /**
       * Remove an existing allow/block list entry by its ID.
       * @param payload - Object containing the item to remove.
       */
      async removeFromAllowBlockList(payload: { item: DeviceAllowBlockListResponseItem }) {
        this.loading = true;
        try {
          const request = {
            ...api.mspEndpointSecurityAllowBlockListItem(payload.item.id),
            method: "DELETE",
          };
          await axiosInstance.request(request);
          await this.getItems(); // Refresh list after deleting
        } catch (error) {
          console.error(error);
        } finally {
          this.loading = false;
        }
      },
    },
  });
};
